import { RightOutlined } from "@ant-design/icons";
import type { CardProps } from "antd";
import { Typography } from "antd";
import i18n from "i18next";
import { type FC } from "react";
import { useNavigate } from "react-router";

import { WelcomeCardContainer, WelcomeCardFlexStyle, WelcomeCardTitle } from "src/app/features/Dashboard/utils/dashboardStyles";
import { PrimaryButton } from "../../../../components/Buttons/components/ActionButton";
import { ResourceName } from "../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../core/router/types/routerTypes";
import type { ApplicationInfoDto, CampaignMetricsDto, OrgUnitMetricsDto, TerminalMetricsDto } from "../types/Dashboard";

/**
 * WelcomeCard - Clean and simple welcome section with background illustration
 */
interface WelcomeCardProps extends CardProps {
    applicationInfo: ApplicationInfoDto;
    campaignMetrics: CampaignMetricsDto;
    orgUnitMetrics: OrgUnitMetricsDto;
    terminalMetrics: TerminalMetricsDto;
}

export const WelcomeCard: FC<WelcomeCardProps> = ({ applicationInfo, campaignMetrics, orgUnitMetrics, terminalMetrics, ...cardProps }) => {
    const navigate = useNavigate();

    const handleGetStarted = () => {
        navigate(`${BASE_PATH}/${ResourceName.CAMPAIGNS}`);
    };

    const running = campaignMetrics.runningCampaignsCount;
    const sites = orgUnitMetrics.totalSitesCount;
    const affected = terminalMetrics.affectedTerminalsCount;

    // Build welcome message based on running campaigns and affected terminals
    const renderMessage = () => {
        if (running === 0 && affected === 0) {
            return <Typography.Text>{i18n.t("dashboard.welcomeCard.sitesText", { sites })}</Typography.Text>;
        }

        if (running === 0 && affected > 0) {
            return <Typography.Text>{i18n.t("dashboard.welcomeCard.terminalsSitesText", { sites, terminals: affected })}</Typography.Text>;
        }

        if (running > 0 && affected === 0) {
            return <Typography.Text>{i18n.t("dashboard.welcomeCard.campaignsSitesText", { sites, campaigns: running })}</Typography.Text>;
        }

        // running > 0 && affected > 0
        return (
            <Typography.Text>
                {i18n.t("dashboard.welcomeCard.campaignsTerminalsSitesText", { sites, campaigns: running, terminals: affected })}
            </Typography.Text>
        );
    };

    return (
        <WelcomeCardContainer {...cardProps}>
            <WelcomeCardFlexStyle justify="space-between" align="center" gap="middle">
                <WelcomeCardFlexStyle vertical gap="large" align="flex-start">
                    <WelcomeCardTitle level={4}>
                        {i18n.t("dashboard.welcomeCard.titleWelcome")}, {applicationInfo.userName.split("@")[0] || applicationInfo.userName}{" "}
                        !
                    </WelcomeCardTitle>
                    {renderMessage()}
                    <PrimaryButton type="primary" size="middle" onClick={handleGetStarted}>
                        {i18n.t("dashboard.getStartedButton")}
                        <RightOutlined />
                    </PrimaryButton>
                </WelcomeCardFlexStyle>
            </WelcomeCardFlexStyle>
        </WelcomeCardContainer>
    );
};
